<script setup>
import { ref, computed, onMounted, onUnmounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import AgentsAPI from 'dashboard/api/agents';
import OnboardingAPI from 'dashboard/api/onboarding';
import Input from 'dashboard/components-next/input/Input.vue';
import Button from 'dashboard/components-next/button/Button.vue';
import SelectMenu from 'dashboard/components-next/selectmenu/SelectMenu.vue';
import { emitter } from 'shared/helpers/mitt';
import { BUS_EVENTS } from 'shared/constants/busEvents';

const props = defineProps({
  progress: { type: Object, required: true },
});
const emit = defineEmits(['refresh', 'update:can-advance']);

const { t } = useI18n();

const currentUserId = useMapGetter('getCurrentUserID');

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const ROLE_OPTIONS = [
  { value: 'agent', label: t('AGENT_MGMT.AGENT_TYPES.AGENT') },
  { value: 'administrator', label: t('AGENT_MGMT.AGENT_TYPES.ADMINISTRATOR') },
];
const roleLabel = value =>
  ROLE_OPTIONS.find(option => option.value === value)?.label;

const isStepAlreadyCompleted = computed(() =>
  props.progress.completed_steps.includes(2)
);

// The same row list doubles as the display for already-invited agents (status
// "success", carrying the real id) and the inputs for new ones — no separate list.
const emptyRow = () => ({
  id: null,
  name: '',
  email: '',
  role: 'agent',
  confirmed: false,
  status: null,
  error: null,
});

const agentRow = agent => ({
  id: agent.id,
  name: agent.name,
  email: agent.email,
  role: agent.role,
  confirmed: agent.confirmed,
  status: 'success',
  error: null,
});

const rows = ref([emptyRow()]);

const verifiedAdministrators = computed(() =>
  rows.value.filter(row => row.role === 'administrator' && row.confirmed)
);

const canRemoveRow = row => {
  if (!row.id) return true;
  if (row.id === currentUserId.value) return false;
  if (!row.confirmed) return true;
  if (row.role === 'administrator') {
    return verifiedAdministrators.value.length !== 1;
  }
  return true;
};

const addRow = () => {
  rows.value.push(emptyRow());
};

const isRowValid = row => row.name.trim() && EMAIL_PATTERN.test(row.email);

const markStepCompleted = async () => {
  try {
    await OnboardingAPI.update({
      completed_steps: [...new Set([...props.progress.completed_steps, 2])],
    });
    emit('refresh');
  } catch (error) {
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
};

const inviteRow = async index => {
  const row = rows.value[index];
  if (!isRowValid(row) || row.id || row.status === 'saving') return;

  const isFirstInvite = !rows.value.some(r => r.status === 'success');
  row.status = 'saving';
  try {
    const { data } = await AgentsAPI.create({
      name: row.name,
      email: row.email,
      role: row.role,
    });
    row.id = data.id;
    row.confirmed = data.confirmed;
    row.status = 'success';
    row.error = null;
    emit('update:can-advance', true);
    if (isFirstInvite) await markStepCompleted();
  } catch (error) {
    row.status = 'error';
    row.error =
      error?.response?.data?.error || t('ONBOARDING.STEP_2.INVITE_ERROR');
  }
};

// The API only accepts name/role on update — email can't be changed once the
// agent exists, so the email input stays disabled after creation.
const updateRow = async (index, payload) => {
  const row = rows.value[index];
  if (row.status === 'saving') return;

  row.status = 'saving';
  try {
    await AgentsAPI.update(row.id, payload);
    row.status = 'success';
    row.error = null;
  } catch (error) {
    row.status = 'error';
    row.error =
      error?.response?.data?.error || t('ONBOARDING.STEP_2.UPDATE_ERROR');
  }
};

// Rows are saved as soon as they become valid (on blur) — there's no separate
// "Invite"/"Save" button in the design, just Name/Email/Role columns and a
// delete action. Branch on row.id rather than row.status so a retry after a
// failed update doesn't try to create the agent again.
const attemptSave = index => {
  const row = rows.value[index];
  if (!isRowValid(row)) return;
  if (row.id) {
    updateRow(index, { name: row.name });
  } else {
    inviteRow(index);
  }
};

const onRoleChange = index => {
  const row = rows.value[index];
  if (row.id) updateRow(index, { role: row.role });
};

const removeRow = async index => {
  const row = rows.value[index];
  if (row.id) {
    try {
      await AgentsAPI.delete(row.id);
    } catch (error) {
      useAlert(t('ONBOARDING.SAVE_ERROR'));
      return;
    }
  }
  rows.value.splice(index, 1);
  if (rows.value.length === 0) addRow();
};

// Re-syncs the list when an attendant is added/removed/edited from another
// screen (e.g. Settings > Agents) so both views stay live. Draft rows the
// user hasn't saved yet are left alone.
const refreshFromServer = async () => {
  try {
    const { data } = await AgentsAPI.get();
    const draftRows = rows.value.filter(row => !row.id);
    const syncedRows = data.map(agentRow);
    rows.value =
      syncedRows.length || draftRows.length
        ? [...syncedRows, ...draftRows]
        : [emptyRow()];
    emit(
      'update:can-advance',
      syncedRows.length > 0 || draftRows.some(isRowValid)
    );
  } catch (error) {
    // Ignore — a background refresh failing shouldn't disrupt an in-progress edit
  }
};

onMounted(async () => {
  try {
    const { data } = await AgentsAPI.get();
    if (data.length) rows.value = data.map(agentRow);
    emit('update:can-advance', data.length > 0);
    if (data.length && !isStepAlreadyCompleted.value) await markStepCompleted();
  } catch (error) {
    emit('update:can-advance', false);
    useAlert(t('ONBOARDING.SAVE_ERROR'));
  }
  emitter.on(BUS_EVENTS.AGENTS_CHANGED, refreshFromServer);
});

onUnmounted(() => {
  emitter.off(BUS_EVENTS.AGENTS_CHANGED, refreshFromServer);
});
</script>

<template>
  <div
    class="flex flex-col items-center justify-start w-full h-full p-8 md:px-16"
  >
    <div class="flex flex-col items-start w-full max-w-2xl gap-4 text-left">
      <div class="flex items-center flex-wrap gap-3">
        <h1 class="text-2xl font-semibold text-n-slate-12">
          {{ t('ONBOARDING.STEP_2.TITLE') }}
        </h1>
        <a
          href="https://help.leadster.com.br/article/414-atendentes-quem-pode-ver-e-responder-as-conversas"
          target="_blank"
          rel="noopener noreferrer"
          class="flex items-center gap-1 px-3 py-1 text-xs rounded-full shrink-0 bg-n-brand/10 text-n-brand hover:brightness-110"
        >
          <i class="i-lucide-circle-help text-sm" />
          {{ t('ONBOARDING.STEP_2.TUTORIAL_LINK') }}
        </a>
      </div>
      <p class="text-n-slate-12">
        {{ t('ONBOARDING.STEP_2.SUBTITLE') }}
        <span class="font-normal text-n-slate-10">{{
          t('ONBOARDING.REQUIRED_BADGE')
        }}</span>
      </p>
      <p class="text-n-slate-11">
        {{ t('ONBOARDING.STEP_2.DESCRIPTION') }}
        <a
          href="https://help.leadster.com.br/article/414-atendentes-quem-pode-ver-e-responder-as-conversas"
          target="_blank"
          rel="noopener noreferrer"
          class="inline-flex items-center gap-1 text-n-brand hover:underline"
        >
          {{ t('ONBOARDING.STEP_2.ROLES_LINK') }}
          <i class="i-lucide-circle-help text-xs" />
        </a>
      </p>

      <div
        class="grid items-end w-full max-w-2xl grid-cols-[1fr_1fr_9rem_2.5rem] gap-x-3 gap-y-2"
      >
        <span class="text-sm font-medium text-n-slate-12">{{
          t('ONBOARDING.STEP_2.NAME_LABEL')
        }}</span>
        <span class="text-sm font-medium text-n-slate-12">{{
          t('ONBOARDING.STEP_2.EMAIL_LABEL')
        }}</span>
        <span class="text-sm font-medium text-n-slate-12">{{
          t('ONBOARDING.STEP_2.ROLE_LABEL')
        }}</span>
        <span />

        <template v-for="(row, index) in rows" :key="index">
          <Input
            v-model="row.name"
            custom-input-class="no-margin"
            :placeholder="t('ONBOARDING.STEP_2.NAME_PLACEHOLDER')"
            @blur="attemptSave(index)"
          />
          <Input
            v-model="row.email"
            type="email"
            custom-input-class="no-margin"
            :placeholder="t('ONBOARDING.STEP_2.EMAIL_PLACEHOLDER')"
            :disabled="row.status === 'success'"
            @blur="attemptSave(index)"
          />
          <SelectMenu
            v-model="row.role"
            :options="ROLE_OPTIONS"
            :label="roleLabel(row.role)"
            size="md"
            :disabled="row.status === 'success' && !canRemoveRow(row)"
            @update:model-value="onRoleChange(index)"
          />
          <Button
            v-if="canRemoveRow(row)"
            type="button"
            variant="faded"
            color="ruby"
            icon="i-lucide-trash"
            @click="removeRow(index)"
          />
          <span v-else />
          <p
            v-if="row.status === 'error'"
            class="text-sm col-span-full text-n-ruby-9"
          >
            {{ row.error }}
          </p>
        </template>
      </div>

      <Button
        type="button"
        variant="faded"
        icon="i-lucide-plus"
        :label="t('ONBOARDING.STEP_2.ADD_ROW')"
        @click="addRow"
      />
    </div>
  </div>
</template>
